import { useRef, useEffect, useState } from 'react';

// BETA 20.24 — was CloudFront; these room images are the optimized local /rooms/ copies.
const CDN = '/rooms';
const ROOMS = [
  { id: 'brace',            name: 'Brace',          url: `${CDN}/room_brace_cdcd9778.png` },
  { id: 'bungulator',       name: 'Bungulator',      url: `${CDN}/room_bungulator_e94cb9e5.png` },
  { id: 'bungulator_lives', name: 'Bung Lives',      url: `${CDN}/room_bungulator_lives_1a20e73f.png` },
  { id: 'bad_man',          name: 'Bad Man',         url: `${CDN}/room_bad_man_dab1a4ba.png` },
  { id: 'ban_that',         name: 'Ban That',        url: `${CDN}/room_ban_that_5d321390.png` },
  { id: 'bobcat',           name: 'Bobcat',          url: `${CDN}/room_bobcat_e634f440.png` },
  { id: 'bugs',             name: 'Bugs',            url: `${CDN}/room_bugs_df100a66.png` },
  { id: 'boyfriend',        name: 'Boyfriend',       url: `${CDN}/room_boyfriend_f8d874dd.png` },
  { id: '1000_men',         name: '1000 Men',        url: `${CDN}/room_1000_men_9704652c.png` },
];

const RS = 768;
const D = 680;
const SC = D / RS;

interface Tri {
  label: string;
  color: string;
  cx: number; cy: number;
  scale: number;
  angle: number;
}

const BASE_SIZE = 200;
const BASE_VERTS = [
  { x: 0, y: 0 },
  { x: BASE_SIZE, y: 0 },
  { x: 0, y: BASE_SIZE },
];

function getVerts(tri: Tri) {
  const cos = Math.cos(tri.angle);
  const sin = Math.sin(tri.angle);
  return BASE_VERTS.map(v => {
    const sx = v.x * tri.scale;
    const sy = v.y * tri.scale;
    return { x: tri.cx + sx * cos - sy * sin, y: tri.cy + sx * sin + sy * cos };
  });
}

const INIT: Tri[] = [
  { label: 'NW', color: 'rgba(0,160,255,0.30)', cx: 170, cy: 170, scale: 1.0, angle: Math.PI },
  { label: 'NE', color: 'rgba(0,200,160,0.30)', cx: 598, cy: 170, scale: 1.0, angle: -Math.PI / 2 },
  { label: 'SE', color: 'rgba(255,140,0,0.30)',  cx: 598, cy: 598, scale: 1.0, angle: 0 },
  { label: 'SW', color: 'rgba(180,80,255,0.30)', cx: 170, cy: 598, scale: 1.0, angle: Math.PI / 2 },
];

export default function Calibrate() {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const [, kick] = useState(0);
  const st = useRef({
    roomImg: null as HTMLImageElement | null,
    activeRoom: 'brace',
    tris: INIT.map(t => ({ ...t })),
    sel: -1,
    drag: null as null | { i: number; sx: number; sy: number; ox: number; oy: number },
    mouse: { x: 0, y: 0 },
    sim: false,
    enemy: null as { x: number; y: number } | null,
    loop: false,
    raf: 0,
  }).current;

  const ws = (v: number) => v * SC;
  const wu = (v: number) => v / SC;

  function inTri(px: number, py: number, vs: { x: number; y: number }[]) {
    const s = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number) =>
      (ax - cx) * (by - cy) - (bx - cx) * (ay - cy);
    const d1 = s(px, py, vs[0].x, vs[0].y, vs[1].x, vs[1].y);
    const d2 = s(px, py, vs[1].x, vs[1].y, vs[2].x, vs[2].y);
    const d3 = s(px, py, vs[2].x, vs[2].y, vs[0].x, vs[0].y);
    return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
  }

  function hitTri(wx: number, wy: number) {
    for (let i = st.tris.length - 1; i >= 0; i--) {
      if (inTri(wx, wy, getVerts(st.tris[i]))) return i;
    }
    return -1;
  }

  function draw() {
    const cv = cvRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d')!;
    ctx.clearRect(0, 0, D, D);

    if (st.roomImg) ctx.drawImage(st.roomImg, 0, 0, D, D);
    else { ctx.fillStyle = '#1a2218'; ctx.fillRect(0, 0, D, D); ctx.fillStyle = '#3a4a38'; ctx.font = '14px monospace'; ctx.textAlign = 'center'; ctx.fillText('← Pick a room', D / 2, D / 2); ctx.textAlign = 'left'; }

    for (let i = 0; i < st.tris.length; i++) {
      const t = st.tris[i], v = getVerts(t), on = st.sel === i;

      ctx.beginPath(); ctx.moveTo(ws(v[0].x), ws(v[0].y)); ctx.lineTo(ws(v[1].x), ws(v[1].y)); ctx.lineTo(ws(v[2].x), ws(v[2].y)); ctx.closePath();
      ctx.fillStyle = t.color; ctx.fill();
      ctx.strokeStyle = on ? '#ffdd00' : 'rgba(0,200,255,0.7)'; ctx.lineWidth = on ? 3 : 1.5; ctx.stroke();

      ctx.beginPath(); ctx.moveTo(ws(v[1].x), ws(v[1].y)); ctx.lineTo(ws(v[2].x), ws(v[2].y));
      ctx.strokeStyle = on ? '#ffdd00' : 'rgba(0,200,255,0.9)'; ctx.lineWidth = 2; ctx.setLineDash([5, 4]); ctx.stroke(); ctx.setLineDash([]);

      const cx = (v[0].x + v[1].x + v[2].x) / 3, cy = (v[0].y + v[1].y + v[2].y) / 3;
      ctx.fillStyle = on ? '#ffdd00' : 'rgba(0,210,255,0.9)'; ctx.font = `bold ${on ? 13 : 11}px monospace`; ctx.textAlign = 'center'; ctx.fillText(t.label, ws(cx), ws(cy) + 4); ctx.textAlign = 'left';

      if (on) {
        ctx.beginPath(); ctx.arc(ws(t.cx), ws(t.cy), 6, 0, Math.PI * 2); ctx.fillStyle = '#ffdd00'; ctx.fill(); ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.stroke();
        for (let vi = 0; vi < v.length; vi++) { ctx.beginPath(); ctx.arc(ws(v[vi].x), ws(v[vi].y), 4, 0, Math.PI * 2); ctx.fillStyle = vi === 0 ? '#ff4444' : '#00ff88'; ctx.fill(); ctx.strokeStyle = '#000'; ctx.lineWidth = 1; ctx.stroke(); }
      }
    }

    if (st.sim && st.enemy) drawSim(ctx);

    ctx.fillStyle = 'rgba(0,0,0,0.7)'; ctx.fillRect(0, D - 18, 140, 18);
    ctx.fillStyle = '#888'; ctx.font = '10px monospace'; ctx.textAlign = 'left'; ctx.fillText(`(${Math.round(st.mouse.x)}, ${Math.round(st.mouse.y)}) wu`, 4, D - 5);
  }

  function drawSim(ctx: CanvasRenderingContext2D) {
    if (!st.enemy) return;
    const ex = st.enemy.x, ey = st.enemy.y, px = st.mouse.x, py = st.mouse.y;
    let hit: Tri | null = null, hv: { x: number; y: number }[] | null = null;
    for (const t of st.tris) { const v = getVerts(t); if (inTri(ex, ey, v)) { hit = t; hv = v; break; } }
    let tx = px, ty = py;
    if (hit && hv) { const d1 = (ex - hv[1].x) ** 2 + (ey - hv[1].y) ** 2, d2 = (ex - hv[2].x) ** 2 + (ey - hv[2].y) ** 2; const p = d1 <= d2 ? hv[1] : hv[2]; tx = p.x; ty = p.y; }
    const dx = tx - ex, dy = ty - ey, d = Math.sqrt(dx * dx + dy * dy);
    if (d > 4) { st.enemy.x += (dx / d) * 2; st.enemy.y += (dy / d) * 2; }

    ctx.beginPath(); ctx.moveTo(ws(ex), ws(ey)); ctx.lineTo(ws(tx), ws(ty));
    ctx.strokeStyle = hit ? 'rgba(255,200,0,0.5)' : 'rgba(255,80,80,0.4)'; ctx.lineWidth = 1.5; ctx.setLineDash([4, 4]); ctx.stroke(); ctx.setLineDash([]);
    ctx.beginPath(); ctx.arc(ws(ex), ws(ey), 10, 0, Math.PI * 2); ctx.fillStyle = hit ? '#ff8800' : '#ff3333'; ctx.fill(); ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#fff'; ctx.font = 'bold 9px monospace'; ctx.textAlign = 'center'; ctx.fillText('E', ws(ex), ws(ey) + 3);
    ctx.beginPath(); ctx.arc(ws(px), ws(py), 9, 0, Math.PI * 2); ctx.fillStyle = 'rgba(0,200,255,0.9)'; ctx.fill(); ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#000'; ctx.font = 'bold 9px monospace'; ctx.fillText('P', ws(px), ws(py) + 3); ctx.textAlign = 'left';
  }

  function loadRoom(r: typeof ROOMS[0]) {
    st.activeRoom = r.id;
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => { st.roomImg = img; draw(); kick(n => n + 1); };
    img.onerror = () => { st.roomImg = null; draw(); };
    img.src = r.url;
  }

  function exportData() {
    const lines = [`// GOOP ZONE — Triangle Calibration (${st.activeRoom})`, ''];
    for (const t of st.tris) {
      const v = getVerts(t);
      lines.push(`// ${t.label}: center(${Math.round(t.cx)},${Math.round(t.cy)}) scale:${t.scale.toFixed(2)} angle:${(t.angle * 180 / Math.PI).toFixed(1)}°`);
      lines.push(`//   apex(${Math.round(v[0].x)},${Math.round(v[0].y)}) legA(${Math.round(v[1].x)},${Math.round(v[1].y)}) legB(${Math.round(v[2].x)},${Math.round(v[2].y)})`);
    }
    lines.push('', 'const CORNER_TRIANGLES = {');
    for (const t of st.tris) {
      const v = getVerts(t);
      lines.push(`  ${t.label}: [{ x:${Math.round(v[0].x)}, y:${Math.round(v[0].y)} }, { x:${Math.round(v[1].x)}, y:${Math.round(v[1].y)} }, { x:${Math.round(v[2].x)}, y:${Math.round(v[2].y)} }],`);
    }
    lines.push('};');
    return lines.join('\n');
  }

  useEffect(() => {
    const cv = cvRef.current!;
    function onDown(e: MouseEvent) {
      const r = cv.getBoundingClientRect(), px = e.clientX - r.left, py = e.clientY - r.top, wx = wu(px), wy = wu(py);
      if (st.sim) { st.enemy = { x: wx, y: wy }; if (!st.loop) { st.loop = true; const f = () => { if (!st.loop) return; draw(); st.raf = requestAnimationFrame(f); }; f(); } return; }
      const h = hitTri(wx, wy);
      if (h >= 0) { st.sel = h; st.drag = { i: h, sx: wx, sy: wy, ox: st.tris[h].cx, oy: st.tris[h].cy }; cv.style.cursor = 'grabbing'; }
      else st.sel = -1;
      draw(); kick(n => n + 1);
    }
    function onMove(e: MouseEvent) {
      const r = cv.getBoundingClientRect(), px = e.clientX - r.left, py = e.clientY - r.top; st.mouse = { x: wu(px), y: wu(py) };
      if (st.drag) { const d = st.drag; st.tris[d.i].cx = d.ox + (st.mouse.x - d.sx); st.tris[d.i].cy = d.oy + (st.mouse.y - d.sy); draw(); kick(n => n + 1); }
      else { cv.style.cursor = hitTri(st.mouse.x, st.mouse.y) >= 0 ? 'grab' : (st.sim ? 'crosshair' : 'default'); if (st.sim || st.sel >= 0) draw(); }
    }
    function onUp() { st.drag = null; cv.style.cursor = 'default'; }
    function onWheel(e: WheelEvent) {
      e.preventDefault(); if (st.sel < 0) return; const t = st.tris[st.sel];
      if (e.shiftKey) t.angle += e.deltaY > 0 ? 0.05 : -0.05;
      else t.scale = Math.max(0.2, Math.min(4.0, t.scale + (e.deltaY > 0 ? -0.05 : 0.05)));
      draw(); kick(n => n + 1);
    }
    cv.addEventListener('mousedown', onDown); cv.addEventListener('mousemove', onMove); cv.addEventListener('mouseup', onUp); cv.addEventListener('mouseleave', onUp); cv.addEventListener('wheel', onWheel, { passive: false });
    loadRoom(ROOMS[0]);
    return () => { cv.removeEventListener('mousedown', onDown); cv.removeEventListener('mousemove', onMove); cv.removeEventListener('mouseup', onUp); cv.removeEventListener('mouseleave', onUp); cv.removeEventListener('wheel', onWheel); st.loop = false; if (st.raf) cancelAnimationFrame(st.raf); };
  }, []);

  const sel = st.sel >= 0 ? st.tris[st.sel] : null;
  const box: React.CSSProperties = { background: '#141414', border: '1px solid #252525', borderRadius: '4px', padding: '10px' };
  const lbl: React.CSSProperties = { fontSize: '9px', color: '#555', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' };
  const sBtn = (active?: boolean): React.CSSProperties => ({ background: active ? '#0a1a22' : '#1a1a1a', border: `1px solid ${active ? '#0cf' : '#2a2a2a'}`, color: active ? '#0cf' : '#888', padding: '4px 5px', borderRadius: '3px', cursor: 'pointer', fontSize: '8px', fontFamily: 'inherit', textAlign: 'center' });
  const ctrlBtn: React.CSSProperties = { background: '#222', border: '1px solid #444', color: '#aaa', width: '24px', height: '24px', borderRadius: '3px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const inputStyle: React.CSSProperties = { background: '#0a0a0a', border: '1px solid #333', color: '#0cf', width: '70px', padding: '3px 6px', borderRadius: '3px', fontFamily: 'inherit', fontSize: '11px', textAlign: 'right', outline: 'none' };

  return (
    <div style={{ background: '#0c0c0c', minHeight: '100vh', color: '#ddd', fontFamily: "'Share Tech Mono',monospace", padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      <div style={{ fontSize: '12px', color: '#0cf', letterSpacing: '3px', textTransform: 'uppercase' }}>⬡ Room Calibrator</div>

      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', width: '100%', maxWidth: '1060px' }}>
        <div>
          <canvas ref={cvRef} width={D} height={D} style={{ border: '1px solid #2a2a2a', display: 'block' }} />
          <div style={{ fontSize: '9px', color: '#444', marginTop: '3px', textAlign: 'center' }}>
            Click to select &nbsp;|&nbsp; Drag to move &nbsp;|&nbsp; Scroll = scale &nbsp;|&nbsp; Shift+Scroll = rotate
          </div>
        </div>

        <div style={{ width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: 'calc(100vh - 60px)' }}>
          {/* Rooms */}
          <div style={box}>
            <div style={lbl}>Room</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
              {ROOMS.map(r => <button key={r.id} onClick={() => loadRoom(r)} style={sBtn(st.activeRoom === r.id)}>{r.name}</button>)}
            </div>
          </div>

          {/* Selected */}
          <div style={{ ...box, borderColor: sel ? '#ffdd00' : '#252525' }}>
            <div style={lbl}>{sel ? `Selected: ${sel.label}` : 'Click a triangle'}</div>
            {sel ? (
              <div style={{ fontSize: '10px', color: '#aaa' }}>
                {/* Position X/Y */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span>X</span>
                  <input type="number" value={Math.round(sel.cx)} onChange={e => { sel.cx = Number(e.target.value) || 0; draw(); kick(n => n + 1); }} style={inputStyle} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span>Y</span>
                  <input type="number" value={Math.round(sel.cy)} onChange={e => { sel.cy = Number(e.target.value) || 0; draw(); kick(n => n + 1); }} style={inputStyle} />
                </div>
                {/* Scale */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span>Scale</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button onClick={() => { sel.scale = Math.max(0.1, sel.scale - 0.05); draw(); kick(n => n + 1); }} style={ctrlBtn}>−</button>
                    <input type="number" step="0.01" value={sel.scale.toFixed(2)} onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { sel.scale = Math.max(0.1, Math.min(5, v)); draw(); kick(n => n + 1); } }} style={{ ...inputStyle, width: '60px' }} />
                    <button onClick={() => { sel.scale = Math.min(5, sel.scale + 0.05); draw(); kick(n => n + 1); }} style={ctrlBtn}>+</button>
                  </div>
                </div>
                {/* Rotate */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span>Angle</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button onClick={() => { sel.angle -= 0.02; draw(); kick(n => n + 1); }} style={ctrlBtn}>↺</button>
                    <input type="number" step="0.1" value={(sel.angle * 180 / Math.PI).toFixed(1)} onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { sel.angle = v * Math.PI / 180; draw(); kick(n => n + 1); } }} style={{ ...inputStyle, width: '60px' }} />
                    <button onClick={() => { sel.angle += 0.02; draw(); kick(n => n + 1); }} style={ctrlBtn}>↻</button>
                  </div>
                </div>
                <div style={{ fontSize: '8px', color: '#444', marginTop: '2px' }}>Type exact values or use buttons. Angle in degrees.</div>
              </div>
            ) : (
              <div style={{ fontSize: '10px', color: '#555' }}>Click any triangle on the canvas.</div>
            )}
          </div>

          {/* All 4 */}
          <div style={box}>
            <div style={lbl}>Triangles</div>
            {st.tris.map((t, i) => (
              <div key={t.label} onClick={() => { st.sel = i; draw(); kick(n => n + 1); }}
                style={{ padding: '4px 6px', marginBottom: '3px', background: st.sel === i ? '#1a2a18' : '#111', border: `1px solid ${st.sel === i ? '#ffdd00' : '#1e1e1e'}`, borderRadius: '3px', cursor: 'pointer', fontSize: '9px', color: '#aaa' }}>
                <b style={{ color: st.sel === i ? '#ffdd00' : '#0cf' }}>{t.label}</b>
                <span style={{ color: '#555', marginLeft: '6px' }}>({Math.round(t.cx)},{Math.round(t.cy)}) s:{t.scale.toFixed(1)} r:{(t.angle * 180 / Math.PI).toFixed(0)}°</span>
              </div>
            ))}
            <button onClick={() => { st.tris.splice(0, 4, ...INIT.map(t => ({ ...t }))); st.sel = -1; draw(); kick(n => n + 1); }}
              style={{ width: '100%', marginTop: '4px', background: '#1a1a1a', border: '1px solid #333', color: '#666', padding: '5px', borderRadius: '3px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '9px' }}>Reset All</button>
          </div>

          {/* Sim */}
          <div style={box}>
            <div style={lbl}>Simulate</div>
            <button onClick={() => { st.sim = !st.sim; if (!st.sim) { st.loop = false; st.enemy = null; } draw(); kick(n => n + 1); }}
              style={{ width: '100%', background: st.sim ? '#2a1a0a' : '#0a2030', border: `1px solid ${st.sim ? '#f80' : '#0cf'}`, color: st.sim ? '#f80' : '#0cf', padding: '7px', borderRadius: '3px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '10px' }}>
              {st.sim ? '⏹ Stop' : '▶ Start'}
            </button>
            {st.sim && <div style={{ fontSize: '9px', color: '#666', marginTop: '4px' }}>Click = place enemy. Mouse = player.</div>}
          </div>

          {/* Export */}
          <div style={box}>
            <div style={lbl}>Export</div>
            <button id="copy-btn2" onClick={() => {
              navigator.clipboard.writeText(exportData()).then(() => {
                const b = document.getElementById('copy-btn2');
                if (b) { b.textContent = '✓ Copied!'; setTimeout(() => { b.textContent = '⎘ Copy to Clipboard'; }, 2000); }
              });
            }} style={{ width: '100%', background: '#0a2030', border: '1px solid #0cf', color: '#0cf', padding: '7px', borderRadius: '3px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '10px', letterSpacing: '1px' }}>
              ⎘ Copy to Clipboard
            </button>
            <div style={{ fontSize: '9px', color: '#555', marginTop: '4px' }}>Copies all 4 triangle vertices as TypeScript.</div>
          </div>

          {/* Controls */}
          <div style={box}>
            <div style={lbl}>Controls</div>
            <div style={{ fontSize: '9px', color: '#666', lineHeight: 1.8 }}>
              <b style={{ color: '#aaa' }}>Click</b> triangle → select<br />
              <b style={{ color: '#aaa' }}>Drag</b> → move<br />
              <b style={{ color: '#aaa' }}>Scroll</b> → scale<br />
              <b style={{ color: '#aaa' }}>Shift+Scroll</b> → rotate<br />
              <b style={{ color: '#aaa' }}>+/−/↺/↻</b> → fine adjust
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
